import React, { useState, useCallback, ChangeEvent } from 'react';
import toast from 'react-hot-toast';
import {
  MdSearch, MdLocalShipping, MdReceiptLong, MdWarehouse, MdCheckBox,
  MdCheckBoxOutlineBlank, MdPerson, MdClose, MdPictureAsPdf,
} from 'react-icons/md';
import {
  PageHeader, Card, Field, Input, Textarea, Button, Spinner, Badge, Modal, ConfirmModal,
} from '../../components/common';
import { invoiceAPI } from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import type { Invoice, DespatchItem } from '../../types';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const nowLocal = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ─── Inline Despatch Form ─────────────────────────────────────────────────────
function DespatchForm({ invoice, onSuccess }: { invoice: Invoice; onSuccess: (inv: Invoice) => void }) {
  const [items, setItems] = useState<(DespatchItem & { checked: boolean })[]>(
    // Extra Work is a service line — never dispatched / no gate pass / no stock
    invoice.items.filter(i => i.code !== 'EXTRA').map(i => ({
      product:     i.product,
      code:        i.code,
      description: i.description,
      shortName:   i.shortName,
      qty:         i.qty,
      unit:        i.unit,
      checked:     true,
    }))
  );
   const [date,        setDate]        = useState(nowLocal());
  const [personName,  setPersonName]  = useState('');
  const [description, setDescription] = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [showGateChoice, setShowGateChoice] = useState(false);
  const [wantGatePass,   setWantGatePass]   = useState(true);

  const toggleItem = (idx: number) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, checked: !it.checked } : it));

  const updateQty = (idx: number, val: string) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, qty: parseFloat(val) || 0 } : it));

  const checkedItems = items.filter(i => i.checked && i.qty > 0);

  // Step 1 — validate inputs, then open gate-pass choice modal
  const handleDespatch = () => {
    if (!personName.trim()) return toast.error('Person name is required');
    if (!checkedItems.length) return toast.error('Select at least one item');
    setWantGatePass(true);
    setShowGateChoice(true);
  };

  // Step 2 — user confirms in modal → actual dispatch
  const confirmDespatch = async () => {
    setShowGateChoice(false);
    setSubmitting(true);
    try {
      const res = await invoiceAPI.addDespatch(invoice._id, {
        date, personName: personName.trim(), description,
        items: checkedItems.map(({ checked: _c, ...rest }) => rest),
        generateGatePass: wantGatePass,
      });
      const updated = res.data.data;
      toast.success('Dispatch recorded successfully!');
      onSuccess(updated);

      if (wantGatePass) {
        try {
          const pdfRes = await invoiceAPI.downloadGatePass(invoice._id);
          downloadBlob(pdfRes.data as Blob, /filename="?([^"]+)"?/.exec((pdfRes.headers?.['content-disposition'] as string) || '')?.[1] || `GatePass-${invoice.invoiceNumber}.pdf`);
          toast.success('Gate Pass downloaded!');
        } catch { toast.error('Gate Pass PDF failed. Try from invoice directly.'); }
      }
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Dispatch failed';
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-5 mt-5">
      {/* Items */}
      <Card className="p-5">
        <h3 className="text-xs font-bold uppercase tracking-widest text-ink-3 mb-3">
          Select Items to Despatch
        </h3>
        <div className="border border-ink-5 rounded-sm overflow-hidden" style={{ borderRadius: 'var(--r-xs)' }}>
          <table className="w-full">
            <thead>
              <tr className="bg-p-pale">
                <th className="py-2 px-3 w-8"></th>
                <th className="py-2 px-3 text-left text-xs font-semibold text-ink-3">Code</th>
                <th className="py-2 px-3 text-left text-xs font-semibold text-ink-3">Product</th>
                <th className="py-2 px-3 text-center text-xs font-semibold text-ink-3 w-32">Qty</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr
                  key={idx}
                  className={`border-t border-ink-5 cursor-pointer transition-colors ${item.checked ? 'bg-green-50' : 'bg-white hover:bg-p-pale'}`}
                  onClick={() => toggleItem(idx)}
                >
                  <td className="py-2.5 px-3">
                    {item.checked
                      ? <MdCheckBox size={18} className="text-green-600" />
                      : <MdCheckBoxOutlineBlank size={18} className="text-ink-4" />}
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="font-mono text-xs font-bold text-p-deep">{item.code}</span>
                  </td>
                  <td className="py-2.5 px-3 text-sm text-ink">
                    {item.shortName || item.description.split('\n')[0]}
                  </td>
                  <td className="py-2.5 px-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1 justify-center">
                      <input
                        type="number" min="0.01" step="0.01"
                        value={item.qty}
                        disabled={!item.checked}
                        onChange={e => updateQty(idx, e.target.value)}
                        className="w-16 px-2 py-1 border border-ink-5 rounded text-sm text-center outline-none focus:ring-1 focus:ring-p disabled:opacity-40"
                        style={{ borderRadius: 'var(--r-xs)' }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-ink-4 mt-1">{checkedItems.length} of {items.length} items selected</p>
      </Card>

      {/* Despatch Info */}
      <Card className="p-5">
        <h3 className="text-xs font-bold uppercase tracking-widest text-ink-3 mb-3">Despatch Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Date & Time" required>
            <Input
              type="datetime-local"
              value={date}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setDate(e.target.value)}
            />
          </Field>
          <Field label="Person Name" required>
            <div className="relative">
              <MdPerson size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4" />
              <Input
                placeholder="Who is receiving / dispatching?"
                value={personName}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setPersonName(e.target.value)}
                className="pl-8"
              />
            </div>
          </Field>
          <Field label="Description / Note" className="md:col-span-2">
            <Textarea
              rows={2}
              placeholder="Vehicle number, delivery note, etc. (optional)"
              value={description}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
            />
          </Field>
        </div>
        <div className="flex justify-end mt-4">
          <Button
            onClick={handleDespatch}
            loading={submitting}
            disabled={!personName.trim() || checkedItems.length === 0}
            icon={<MdLocalShipping size={16} />}
          >
            Confirm Despatch
          </Button>
        </div>
      </Card>

      {/* Gate Pass Choice Modal */}
      <Modal
        open={showGateChoice}
        onClose={() => setShowGateChoice(false)}
        title="Confirm Despatch"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowGateChoice(false)}>Cancel</Button>
            <Button onClick={confirmDespatch} loading={submitting} icon={<MdLocalShipping size={16} />}>
              Confirm Despatch
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-ink-2">
            You are about to record a dispatch for invoice <strong>{invoice.invoiceNumber}</strong>.
          </p>
          <label
            className="flex items-center gap-3 cursor-pointer p-3 border border-ink-5 rounded-sm hover:bg-p-pale transition-colors"
            style={{ borderRadius: 'var(--r-xs)' }}
          >
            <input
              type="checkbox"
              checked={wantGatePass}
              onChange={e => setWantGatePass(e.target.checked)}
              className="w-4 h-4"
              style={{ accentColor: 'var(--p)' }}
            />
            <div>
              <p className="text-sm font-semibold text-ink">Generate Gate Pass</p>
              <p className="text-xs text-ink-4">Gate pass PDF will be downloaded after dispatch.</p>
            </div>
          </label>
        </div>
      </Modal>
    </div>
  );
}

// ─── Invoice Info Panel ───────────────────────────────────────────────────────
function InvoicePanel({ invoice, onDespatched }: { invoice: Invoice; onDespatched: (inv: Invoice) => void }) {
  const [pdfLoading,  setPdfLoading]  = useState(false);
  const [gateLoading, setGateLoading] = useState(false);
  const [showGateWarning, setShowGateWarning] = useState(false);
  const isDispatched = invoice.despatchStatus === 'dispatched';

  const downloadPDF = async () => {
    setPdfLoading(true);
    try {
      const res = await invoiceAPI.downloadPDF(invoice._id);
      downloadBlob(res.data as Blob, /filename="?([^"]+)"?/.exec((res.headers?.['content-disposition'] as string) || '')?.[1] || `Invoice-${invoice.invoiceNumber}.pdf`);
    } catch { toast.error('Failed to download invoice PDF'); }
    finally { setPdfLoading(false); }
  };

  // Step 1 — open warning modal
  const openGatePassConfirm = () => setShowGateWarning(true);

  // Step 2 — confirmed → actually download
  const downloadGatePass = async () => {
    setShowGateWarning(false);
    setGateLoading(true);
    try {
      const res = await invoiceAPI.downloadGatePass(invoice._id);
      downloadBlob(res.data as Blob, /filename="?([^"]+)"?/.exec((res.headers?.['content-disposition'] as string) || '')?.[1] || `GatePass-${invoice.invoiceNumber}.pdf`);
      onDespatched({
        ...invoice,
        despatch: {
          ...invoice.despatch!,
          gatePassGenerated:   true,
          gatePassGeneratedBy: 'You',
          gatePassGeneratedAt: new Date().toISOString(),
        }
      });
    } catch { toast.error('Failed to download gate pass'); }
    finally { setGateLoading(false); }
  };

  return (
    <div className="mt-5 space-y-4">
      {/* Invoice Header Card */}
      <Card className="p-5">
        <div className="flex items-start justify-between mb-4 pb-4 border-b border-ink-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--p-pale)' }}>
              <MdReceiptLong size={20} className="text-p" />
            </div>
            <div>
              <div className="text-base font-bold text-ink">{invoice.invoiceNumber}</div>
              <div className="text-xs text-ink-4">{formatDate(invoice.date)}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Badge variant={isDispatched ? 'success' : 'warning'}>
              {isDispatched ? '✓ Dispatched' : '⏳ Pending Despatch'}
            </Badge>
            <Button size="sm" variant="secondary" onClick={downloadPDF} loading={pdfLoading} icon={<MdPictureAsPdf size={14} />}>
              Invoice PDF
            </Button>
            {isDispatched && (
              invoice.despatch?.gatePassGenerated ? (
                <div className="flex flex-col items-end gap-0.5">
                  <button disabled className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-400 bg-gray-100 rounded-md cursor-not-allowed border border-gray-200">
                    <MdPictureAsPdf size={14} /> Gate Pass (Generated)
                  </button>
                  <span className="text-xs text-ink-4">
                    by {invoice.despatch.gatePassGeneratedBy || 'Unknown'}
                    {invoice.despatch.gatePassGeneratedAt ? ` · ${new Date(invoice.despatch.gatePassGeneratedAt).toLocaleString('en-PK', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
                  </span>
                </div>
               ) : (
                <Button size="sm" variant="secondary" onClick={openGatePassConfirm} loading={gateLoading} icon={<MdPictureAsPdf size={14} />}>
                  Gate Pass
                </Button>
              )
            )}
          </div>
        </div>

        {/* Client & Reference Numbers */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="p-3 bg-p-pale rounded-sm text-center" style={{ borderRadius: 'var(--r-xs)' }}>
            <div className="text-xs text-ink-4 uppercase tracking-wider">Client</div>
            <div className="font-semibold text-ink text-sm mt-0.5">{invoice.clientSnapshot.name}</div>
          </div>
          <div className="p-3 bg-p-pale rounded-sm text-center" style={{ borderRadius: 'var(--r-xs)' }}>
            <div className="text-xs text-ink-4 uppercase tracking-wider">Quotation #</div>
            <div className="font-mono font-bold text-p-deep text-sm mt-0.5">{invoice.quotationNumber}</div>
          </div>
          <div className="p-3 bg-p-pale rounded-sm text-center" style={{ borderRadius: 'var(--r-xs)' }}>
            <div className="text-xs text-ink-4 uppercase tracking-wider">Order #</div>
            <div className="font-mono font-bold text-p-deep text-sm mt-0.5">{invoice.orderNumber}</div>
          </div>
          <div className="p-3 bg-p-pale rounded-sm text-center" style={{ borderRadius: 'var(--r-xs)' }}>
            <div className="text-xs text-ink-4 uppercase tracking-wider">Grand Total</div>
            <div className="font-bold text-ink text-sm mt-0.5">{formatCurrency(invoice.grandTotal)}</div>
          </div>
        </div>

        {/* Items Table */}
        <div className="border border-ink-5 rounded-sm overflow-hidden" style={{ borderRadius: 'var(--r-xs)' }}>
          <table className="w-full">
            <thead>
              <tr className="bg-p-pale">
                <th className="py-2 px-3 text-left text-xs font-semibold text-ink-3">Code</th>
                <th className="py-2 px-3 text-left text-xs font-semibold text-ink-3">Product</th>
                <th className="py-2 px-3 text-center text-xs font-semibold text-ink-3">Qty</th>
                <th className="py-2 px-3 text-right text-xs font-semibold text-ink-3">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, idx) => (
                <tr key={idx} className="border-t border-ink-5">
                  <td className="py-2 px-3"><span className="font-mono text-xs font-bold text-p-deep">{item.code}</span></td>
                  <td className="py-2 px-3 text-sm text-ink">{item.shortName || item.description.split('\n')[0]}</td>
                  <td className="py-2 px-3 text-sm text-center text-ink-2">{item.qty}</td>
                  <td className="py-2 px-3 text-sm text-right font-semibold text-ink">{formatCurrency(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Dispatched Record */}
      {isDispatched && invoice.despatch ? (
        <div className="p-4 rounded-lg bg-green-50 border border-green-200" style={{ borderRadius: 'var(--r-xs)' }}>
          <div className="flex items-center gap-2 mb-3">
            <MdLocalShipping size={18} className="text-green-700" />
            <span className="text-sm font-bold text-green-800">Despatch Record</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
            <div>
              <div className="text-xs text-green-600 font-semibold uppercase tracking-wider">Date & Time</div>
              <div className="text-sm font-medium text-green-900 mt-0.5">
                {new Date(invoice.despatch.date).toLocaleString('en-PK', { dateStyle: 'medium', timeStyle: 'short' })}
              </div>
            </div>
            <div>
              <div className="text-xs text-green-600 font-semibold uppercase tracking-wider">Dispatched To</div>
              <div className="text-sm font-bold text-green-900 mt-0.5">{invoice.despatch.personName}</div>
            </div>
            {invoice.despatch.description && (
              <div>
                <div className="text-xs text-green-600 font-semibold uppercase tracking-wider">Note</div>
                <div className="text-sm text-green-900 mt-0.5">{invoice.despatch.description}</div>
              </div>
            )}
          </div>
          <div className="border border-green-200 rounded-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-green-100">
                  <th className="py-1.5 px-3 text-left text-xs font-semibold text-green-700">Code</th>
                  <th className="py-1.5 px-3 text-left text-xs font-semibold text-green-700">Product</th>
                  <th className="py-1.5 px-3 text-center text-xs font-semibold text-green-700">Qty</th>
                </tr>
              </thead>
              <tbody>
                {invoice.despatch.items.map((di, idx) => (
                  <tr key={idx} className="border-t border-green-200">
                    <td className="py-1.5 px-3 font-mono text-xs font-bold text-green-800">{di.code}</td>
                    <td className="py-1.5 px-3 text-xs text-green-900">{di.shortName || di.description}</td>
                    <td className="py-1.5 px-3 text-xs text-center text-green-900">{di.qty} {di.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {invoice.despatch.gatePassGenerated && (
            <div className="mt-2 text-xs text-green-700">✓ Gate Pass was generated for this despatch</div>
          )}
        </div>
      ) : (
        /* Pending — show dispatch form */
        <DespatchForm invoice={invoice} onSuccess={onDespatched} />
      )}

      {/* Gate Pass One-Time Warning Modal */}
      <ConfirmModal
        open={showGateWarning}
        onClose={() => setShowGateWarning(false)}
        onConfirm={downloadGatePass}
        title="⚠️ Gate Pass — One Time Only"
        message="Are you sure you want to generate the Gate Pass? Important: A Gate Pass can only be generated ONCE for this dispatch. After generation, this button will be permanently disabled and cannot be used again."
        loading={gateLoading}
        danger
      />
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function DispatchPage() {
  const [searchInput, setSearchInput] = useState('');
  const [searching,   setSearching]   = useState(false);
  const [invoice,     setInvoice]     = useState<Invoice | null>(null);
  const [notFound,    setNotFound]    = useState(false);

  const handleSearch = useCallback(async () => {
    const q = searchInput.trim();
    if (!q) { toast.error('Enter an Invoice, Quotation or Order number'); return; }

    setSearching(true);
    setInvoice(null);
    setNotFound(false);

    try {
      const res = await invoiceAPI.searchByNumber(q);
      const found = res.data.data;
      if (found) {
        setInvoice(found);
      } else {
        setNotFound(true);
        toast.error('No invoice found for that number');
      }
    } catch {
      setNotFound(true);
      toast.error('No invoice found for that number');
    } finally { setSearching(false); }
  }, [searchInput]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleClear = () => {
    setSearchInput('');
    setInvoice(null);
    setNotFound(false);
  };

  return (
    <div>
      <PageHeader
        title="Dispatch"
        subtitle="Search by Invoice Number, Quotation Number or Order Number to dispatch items"
      />

      {/* Search Bar */}
      <Card className="p-5 mb-4">
        <div className="flex gap-3 items-end">
          <Field label="Search Invoice" className="flex-1">
            <div className="relative">
              <MdSearch size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4" />
              <Input
                placeholder="e.g. INV-202504-0001  or  Q-1042  or  ODR-202504-0012"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="pl-9"
              />
            </div>
          </Field>
          <Button onClick={handleSearch} loading={searching} icon={<MdSearch size={16} />}>
            Search
          </Button>
          {(invoice || notFound) && (
            <Button variant="secondary" onClick={handleClear} icon={<MdClose size={16} />}>
              Clear
            </Button>
          )}
        </div>
      </Card>

      {/* Loading */}
      {searching && (
        <div className="flex items-center justify-center py-16 gap-3">
          <Spinner size={24} />
          <span className="text-ink-3 text-sm">Searching...</span>
        </div>
      )}

      {/* Not Found */}
      {!searching && notFound && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-red-50">
            <MdSearch size={32} className="text-red-400" />
          </div>
          <div className="text-base font-semibold text-ink-3">No Invoice Found</div>
          <div className="text-sm text-ink-4 mt-1">Try a different Invoice / Quotation / Order number</div>
        </div>
      )}

      {/* Empty Initial State */}
      {!searching && !invoice && !notFound && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: 'var(--p-pale)' }}>
            <MdWarehouse size={32} className="text-p-deep opacity-40" />
          </div>
          <div className="text-base font-semibold text-ink-3">Dispatch Items</div>
          <div className="text-sm text-ink-4 mt-1">Enter any reference number above to find the invoice</div>
        </div>
      )}

      {/* Invoice Panel */}
      {!searching && invoice && (
        <InvoicePanel invoice={invoice} onDespatched={(updated) => setInvoice(updated)} />
      )}
    </div>
  );
}